/**
 * Mock answer library + image/video library (docs/library.md). Texts are the shop's own approved wording
 * (api/app/ai/policy.py canned_catalog, ai_answers 0007/0008) — no invented prices or menus. The pictures are
 * neutral drawings standing in for the owner's real menu images.
 */
import type { AnswerTypeInfo, CannedItem, MediaAsset, Placeholder } from './types'

export const MOCK_ORDER_LINK = 'cleanfit.cloud/order'
export const PH_NAME = '{ชื่อลูกค้า}'
export const PH_LINK = '{ลิงก์สั่งซื้อ}'

const svg = (w: number, h: number, body: string): string =>
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">${body}</svg>`)

/** A menu board: header band + dish circles in a grid (portrait 4:5). */
function menuBoard(bg: string, band: string, dishes: string[]): string {
  let cells = ''
  dishes.forEach((c, i) => {
    const x = 150 + (i % 3) * 390
    const y = 470 + Math.floor(i / 3) * 420
    cells +=
      `<circle cx="${x}" cy="${y}" r="120" fill="#fff"/><circle cx="${x}" cy="${y}" r="92" fill="${c}"/>` +
      `<rect x="${x - 110}" y="${y + 150}" width="220" height="22" rx="11" fill="#fff" opacity=".85"/>` +
      `<rect x="${x - 70}" y="${y + 186}" width="140" height="16" rx="8" fill="#fff" opacity=".55"/>`
  })
  return svg(
    1080,
    1350,
    `<rect width="1080" height="1350" fill="${bg}"/><rect width="1080" height="250" fill="${band}"/>` +
      `<rect x="80" y="84" width="520" height="44" rx="22" fill="#fff" opacity=".9"/>` +
      `<rect x="80" y="150" width="320" height="28" rx="14" fill="#fff" opacity=".6"/>${cells}`,
  )
}

const MENU_WEEK = menuBoard('#eef6f0', '#0e7a5c', ['#e9b949', '#9ccf7c', '#d98e5f', '#7fb58f', '#f1d38a', '#c9795c'])
const MENU_JAE = menuBoard('#fbf6e6', '#b8860b', ['#f2d16b', '#9ccf7c', '#e7b76a', '#c7dc8a', '#f5e0a0', '#a8c97a'])
/** The poster of the approved "เมนูรอบนี้" (mock round, api/kb/menuMock.ts) — /menu sends it while the round is in use. */
const MENU_ROUND = menuBoard('#f3f0fa', '#4b3f8f', ['#e9b949', '#d98e5f', '#9ccf7c', '#f2a38a', '#7fb58f', '#c9795c'])
const ROUNDS = svg(
  1280,
  720,
  '<rect width="1280" height="720" fill="#f4f7fb"/><rect width="1280" height="140" fill="#2f5160"/>' +
    '<rect x="70" y="50" width="420" height="40" rx="20" fill="#fff" opacity=".9"/>' +
    [0, 1, 2, 3]
      .map(
        (i) =>
          `<rect x="${70 + i * 290}" y="220" width="250" height="400" rx="24" fill="#fff"/>` +
          `<circle cx="${195 + i * 290}" cy="330" r="62" fill="${['#9fd3c2', '#8fc1e3', '#f3c77b', '#e6a58c'][i]}"/>` +
          `<rect x="${115 + i * 290}" y="440" width="160" height="26" rx="13" fill="#d9e3ea"/>` +
          `<rect x="${135 + i * 290}" y="490" width="120" height="18" rx="9" fill="#e8eef2"/>`,
      )
      .join(''),
)
const FREEZER = svg(
  1080,
  1080,
  '<rect width="1080" height="1080" fill="#eaf4fb"/><rect x="300" y="170" width="480" height="760" rx="48" fill="#fff"/>' +
    '<rect x="300" y="170" width="480" height="300" rx="48" fill="#cfe6f5"/><rect x="340" y="500" width="400" height="12" rx="6" fill="#dbe7ef"/>' +
    '<rect x="370" y="560" width="160" height="120" rx="18" fill="#9ccf7c"/><rect x="550" y="560" width="160" height="120" rx="18" fill="#e9b949"/>' +
    '<rect x="370" y="720" width="160" height="120" rx="18" fill="#d98e5f"/><rect x="550" y="720" width="160" height="120" rx="18" fill="#7fb58f"/>',
)
const REHEAT_POSTER = svg(
  1280,
  720,
  '<rect width="1280" height="720" fill="#1f2a25"/><rect x="330" y="160" width="620" height="400" rx="36" fill="#3b4a43"/>' +
    '<rect x="370" y="200" width="420" height="320" rx="20" fill="#2a3530"/><circle cx="580" cy="360" r="110" fill="#e9b949" opacity=".9"/>' +
    '<rect x="820" y="220" width="90" height="26" rx="13" fill="#6f8279"/><circle cx="865" cy="320" r="30" fill="#6f8279"/>',
)

function media(p: Partial<MediaAsset> & Pick<MediaAsset, 'id' | 'kind' | 'title' | 'folder' | 'url'>, now: number, daysAgo: number): MediaAsset {
  return {
    preview_url: p.url,
    mime: p.kind === 'video' ? 'video/mp4' : 'image/png',
    bytes: 480_000,
    width: 1080,
    height: 1350,
    duration_ms: null,
    created_at: new Date(now - daysAgo * 86400_000).toISOString(),
    created_by: 'ploy',
    created_by_name: 'พลอย',
    used_in: [],
    send_count: 0,
    last_sent_at: null,
    limits: [],
    deleted: false,
    ...p,
  }
}

export function createMockMedia(now: number): MediaAsset[] {
  return [
    media({ id: 'md1', kind: 'image', title: 'เมนูสัปดาห์นี้', folder: 'เมนู', url: MENU_WEEK, send_count: 86 }, now, 2),
    media({ id: 'md2', kind: 'image', title: 'เมนูเจ ช่วงเทศกาล', folder: 'เมนู', url: MENU_JAE, send_count: 12 }, now, 4),
    media(
      { id: 'md3', kind: 'image', title: 'รอบส่ง Lalamove', folder: 'การจัดส่ง', url: ROUNDS, width: 1280, height: 720, send_count: 31 },
      now,
      9,
    ),
    media(
      { id: 'md4', kind: 'video', title: 'วิธีอุ่นด้วยไมโครเวฟ', folder: 'เก็บรักษาและอุ่น', url: 'https://media.example.invalid/m/reheat',
        preview_url: REHEAT_POSTER, bytes: 18_400_000, width: 1280, height: 720, duration_ms: 42_000, send_count: 17,
        created_by: 'siri', created_by_name: 'ศิริ' },
      now,
      12,
    ),
    media(
      { id: 'md5', kind: 'image', title: 'วิธีเก็บในช่องฟรีซ', folder: 'เก็บรักษาและอุ่น', url: FREEZER, width: 1080, height: 1080, send_count: 22 },
      now,
      15,
    ),
    media({ id: 'md6', kind: 'image', title: 'โปสเตอร์เมนูรอบนี้', folder: 'โปสเตอร์เมนู', url: MENU_ROUND, send_count: 0 }, now, 0),
  ]
}

interface CannedSeed {
  id: string
  shortcut: string
  title: string
  body: string
  intents: string[]
  media: string[]
  use: number
  active?: boolean
  /** A starting draft of migration 0053 (switched off until the owner reviews it). */
  draft?: boolean
}

const LINK_TEXT = `สั่งได้ที่ลิงก์นี้เลยค่ะ ${PH_LINK} (สั่งทางเว็บได้ถึง 21.30 น. สำหรับรอบส่งวันถัดไปค่ะ)`

const CANNED_SEEDS: CannedSeed[] = [
  { id: 'cr1', shortcut: 'menu', title: 'เมนูอาหาร', body: LINK_TEXT, intents: ['menu'], media: ['md1'], use: 128 },
  { id: 'cr2', shortcut: 'link', title: 'ส่งลิงก์สั่งซื้อ', body: LINK_TEXT, intents: ['order_link'], media: [], use: 96 },
  { id: 'cr3', shortcut: 'zip', title: 'ขอรหัสไปรษณีย์', body: 'รบกวนขอรหัสไปรษณีย์ด้วยนะคะ จะได้เช็คค่าส่งและรอบส่งให้ค่ะ', intents: [], media: [], use: 74 },
  {
    id: 'cr4', shortcut: 'freezer', title: 'เก็บรักษา: ช่องฟรีซ / ช่องธรรมดา', intents: ['storage'], media: ['md5'], use: 41,
    body: 'เมื่อได้รับอาหารแล้วให้นำเข้าช่องฟรีซทันทีนะคะ เก็บในช่องฟรีซได้ 120 วันค่ะ ถ้าแบ่งไว้ช่องธรรมดาแนะนำให้ทานภายใน 7 วันค่ะ เพราะทางร้านไม่ใส่สารกันเสีย ที่บรรจุภัณฑ์มีวันผลิตและวันหมดอายุบอกไว้ด้วยค่ะ',
  },
  {
    id: 'cr5', shortcut: 'reheat', title: 'วิธีอุ่น', intents: ['storage'], media: ['md4'], use: 33,
    body: 'อุ่นด้วยไมโครเวฟได้เลยค่ะ วางถุงบนภาชนะแล้วตัดปากถุงเล็กน้อย ใช้ไฟกลางประมาณ 2.30-3.30 นาที หรือต้มในน้ำเดือดทั้งถุงโดยไม่ต้องตัดปากถุง นำออกจากช่องแข็งแล้วอุ่นได้ทันทีค่ะ บรรจุภัณฑ์เข้าไมโครเวฟได้ค่ะ',
  },
  {
    id: 'cr6', shortcut: 'cutoff', title: 'สั่งวันนี้ได้ของวันไหน', intents: ['ship_date'], media: [], use: 29,
    body: 'สั่งและชำระกับแอดมินในแชทก่อน 22.00 น. ได้รับอาหารวันถัดไปค่ะ (สั่งล่วงหน้า 1 วัน) ถ้าสั่งเองทางเว็บ ปิดรับรอบวันถัดไปเวลา 21.30 น. จัดส่งทุกวัน ยกเว้นวันเสาร์เนื่องจากครัวปิดค่ะ',
  },
  { id: 'cr7', shortcut: 'phone', title: 'ขอเบอร์ที่ใช้สั่ง', body: 'รบกวนขอเบอร์โทรที่ใช้สั่ง หรือเลขออเดอร์ด้วยนะคะ แอดมินจะเช็คให้ค่ะ', intents: [], media: [], use: 18 },
  { id: 'cr8', shortcut: 'wait', title: 'ขอเวลาเช็ค', body: 'รอสักครู่นะคะ แอดมินขอเช็คข้อมูลให้ก่อนค่ะ', intents: [], media: [], use: 15 },
  { id: 'cr9', shortcut: 'thanks', title: 'ขอบคุณ', body: 'ขอบคุณมากค่ะ มีอะไรสอบถามเพิ่มเติมทักมาได้เลยนะคะ', intents: [], media: [], use: 52 },
  { id: 'cr10', shortcut: 'jae', title: 'เมนูเจ ช่วงเทศกาล', body: LINK_TEXT, intents: [], media: ['md2'], use: 0, active: false },
  // starting drafts of the suggested script (api/app/ai/script/drafts.py, migration 0053) — switched off, not reviewed
  ...([
    ['greeting', 'ทักทาย', 'ทักทาย (ขึ้นต้นคำตอบ)', ['script_greeting'], 'สวัสดีค่ะคุณ{ชื่อลูกค้า}'],
    ['ship_fee', 'ค่าส่ง', 'ค่าส่ง · พื้นที่ส่ง', ['ship_fee', 'ship_area'],
      'ส่ง{พื้นที่ส่ง}ได้ค่ะ ส่งด้วย {ขนส่ง} ค่าส่งรอบละ {ค่าส่ง} ค่ะ {รอบส่ง}นะคะ'],
    ['ship_date', 'วันได้รับ', 'วันได้ของ (รู้พื้นที่แล้ว)', ['ship_date'], 'ถ้าสั่งและชำระ{สั่งภายใน} จะได้รับอาหาร{วันได้รับ} ค่ะ'],
    ['price', 'ราคา', 'ราคาแพ็ก', ['price'],
      'ราคาแพ็กตอนนี้ค่ะ\n{รายการราคา}\nราคานี้ยังไม่รวมค่าส่งนะคะ รวมค่าส่งแล้ว {ราคารวมค่าส่ง} ค่ะ'],
    ['promo', 'โปร', 'โปรโมชั่น', ['promo'], 'ขอบคุณที่สนใจนะคะ เรื่องโปรโมชั่นแอดมินขอเช็กให้ก่อนนะคะ แล้วจะรีบแจ้งกลับค่ะ'],
    ['complaint', 'ร้องเรียน', 'ลูกค้าแจ้งปัญหา', ['complaint'],
      'ต้องขออภัยคุณลูกค้าในความไม่สะดวกด้วยนะคะ แอดมินขอเช็กรายละเอียดให้ก่อน แล้วจะรีบแจ้งกลับค่ะ'],
    ['followup', 'ทักตาม', 'ทักตาม · ลูกค้าเงียบ', ['script_followup'],
      'สวัสดีค่ะคุณ{ชื่อลูกค้า}\n\nถ้าสนใจแพ็กไหน หรือมีคำถามเพิ่มเติม ทักแอดมินได้เลยนะคะ'],
    ['opener_last_pack', 'ลูกค้าเก่าแพ็กเดิม', 'ทักลูกค้าเก่า · แพ็กเดิม', ['script_opener'],
      'สวัสดีค่ะคุณ{ชื่อลูกค้า} ยินดีต้อนรับกลับมานะคะ\n\nรอบนี้รับ{แพ็กเดิม}เหมือนครั้งก่อนไหมคะ'],
  ] as [string, string, string, string[], string][]).map(([key, shortcut, title, intents, body]): CannedSeed => ({
    id: `crd-${key}`, shortcut, title, intents, body, media: [], use: 0, active: false, draft: true,
  })),
  // คู่มือปิดการขาย (api/app/ai/script/drafts.py PLAYBOOK_DRAFTS, migration 0057) — switched ON, still Claude's draft
  ...([
    ['pb_options', 'แพ็กอื่น', 'คู่มือ · ลูกค้าถามตัวเลือก', 'ตอนนี้มีแพ็กให้เลือกตามนี้ค่ะ\n{รายการราคา}\nราคานี้ยังไม่รวมค่าส่งนะคะ'],
    ['pb_price_objection', 'ติดราคา', 'คู่มือ · ลูกค้าติดราคา',
      'เข้าใจเลยค่ะ ถ้าคิดเป็นรายมื้อ แพ็กเริ่มต้นของร้านตามนี้ค่ะ\n{แพ็กเล็กสุด}\n\n' +
        'ถ้าอยากเริ่มแบบเบาๆ ก่อน ร้านมีแพ็ก{แพ็กทดลอง}ให้ลองด้วยนะคะ สนใจลองแพ็กทดลองก่อนไหมคะ'],
    ['pb_hesitate', 'ลังเล', 'คู่มือ · ลูกค้าขอคิดก่อน',
      'ไม่เป็นไรเลยค่ะ ค่อยๆ ตัดสินใจได้นะคะ\n\nระหว่างนี้ จะให้แอดมินสรุปแพ็กที่เหมาะกับคุณลูกค้าส่งไว้ให้ดูก่อนไหมคะ'],
    ['pb_trial', 'ลองก่อน', 'คู่มือ · ลูกค้าอยากลองก่อน',
      'ร้านมีแพ็กทดลองให้ลองทานก่อนค่ะ\n{แพ็กทดลอง}\n\nถ้าถูกใจค่อยต่อแพ็กที่ใหญ่ขึ้นได้นะคะ สนใจเริ่มจากแพ็กทดลองไหมคะ'],
    ['pb_quality', 'คุณภาพ', 'คู่มือ · ลูกค้าถามคุณภาพ',
      'ขอบคุณที่สนใจนะคะ แอดมินอยากให้คุณลูกค้าได้ลองด้วยตัวเองค่ะ ร้านมีแพ็ก{แพ็กทดลอง}ให้ลองทานก่อนได้นะคะ\n\n' +
        'ดูเมนูทั้งหมดพร้อมรายละเอียดได้ที่ {ลิงก์สั่งซื้อ} ค่ะ'],
    ['pb_agree', 'ตกลง', 'คู่มือ · ลูกค้าตกลง', 'รับทราบค่ะ ขอบคุณมากนะคะ'],
  ] as [string, string, string, string][]).map(([key, shortcut, title, body]): CannedSeed => ({
    id: `crp-${key}`, shortcut, title, intents: [key], body, media: [], use: 0, active: true, draft: true,
  })),
]

export function fillPlaceholders(text: string, name: string | null, link = MOCK_ORDER_LINK): string {
  let out = text.split(PH_LINK).join(link)
  if (out.includes(PH_NAME)) {
    const n = (name ?? '').trim()
    const bare = n.replace(/^คุณ\s*/, '')
    if (bare) out = out.split(`คุณ${PH_NAME}`).join(`คุณ${bare}`).split(PH_NAME).join(n)
    else out = out.replace(/\s*คุณ\s*\{ชื่อลูกค้า\}/g, '').split(PH_NAME).join('').replace(/[ \t]{2,}/g, ' ').trim()
  }
  return out
}

export function createMockCanned(media: MediaAsset[], now: number): CannedItem[] {
  const byId = new Map(media.map((m) => [m.id, m]))
  return CANNED_SEEDS.map((s, i) => ({
    id: s.id,
    shortcut: s.shortcut,
    title: s.title,
    body: s.body,
    text: fillPlaceholders(s.body, null),
    intents: s.intents,
    media: s.media.map((id) => byId.get(id)).filter((m): m is MediaAsset => !!m),
    is_active: s.active ?? true,
    is_draft: !!s.draft,
    chat_only: chatOnly(s.body),
    sort: (i + 1) * 10,
    use_count: s.use,
    last_used_at: s.use ? new Date(now - (i + 1) * 3600_000).toISOString() : null,
    updated_at: new Date(now - 3 * 86400_000).toISOString(),
    updated_by_name: 'พลอย',
  }))
}

/** The placeholders of api/app/ai/answers.py (the 2 filled everywhere, then the chat's data of the suggested script). */
export const MOCK_PLACEHOLDERS: Placeholder[] = [
  { key: PH_NAME, label: 'ชื่อลูกค้า', sample: 'คุณมายด์', note: 'ชื่อที่ลูกค้าตั้งในแชท (ไม่มีชื่อ = ตัด “คุณ{ชื่อลูกค้า}” ออกทั้งคำ)', scope: 'all' },
  { key: PH_LINK, label: 'ลิงก์สั่งซื้อ', sample: MOCK_ORDER_LINK, note: 'ลิงก์หน้าเมนูและสั่งซื้อของร้าน', scope: 'all' },
  ...([
    ['{พื้นที่ส่ง}', 'พื้นที่ส่ง', 'เขตบางนา', 'เขต/อำเภอที่ลูกค้าบอก หรือที่อยู่เดิมใน POS'],
    ['{ขนส่ง}', 'ขนส่ง', 'Lalamove', 'ขนส่งที่ระบบค่าส่งเลือก (Lalamove / DHL แบบแช่เย็น)'],
    ['{ค่าส่ง}', 'ค่าส่ง', '110 บาท', 'ค่าส่งต่อรอบจากระบบค่าส่งของเว็บ (ยังไม่รู้พื้นที่ = ไม่ใช้คำตอบนี้ ถามพื้นที่แทน)'],
    ['{รอบส่ง}', 'แพ็กที่ส่งหลายรอบ', 'แพ็ก 30 วันส่ง 2 รอบ', 'แพ็กในคำตอบราคาที่ส่งหลายรอบ (ไม่มี = ตัดประโยคนั้นออก)', true],
    ['{วันได้รับ}', 'วันได้รับ', 'วันอาทิตย์ที่ 18 ต.ค.', 'วันที่ได้รับอาหารเร็วสุดถ้าสั่งตอนนี้ (ปฏิทินส่งของเว็บ)'],
    ['{สั่งภายใน}', 'สั่งภายใน', 'วันนี้ก่อน 22.00 น.', 'สั่งและชำระภายในเมื่อไรจึงได้รับตาม {วันได้รับ}'],
    ['{ปิดรับแชท}', 'ปิดรับในแชท', '22.00 น.', 'เวลาปิดรับรอบส่งวันถัดไปเมื่อสั่งในแชท (ค่าตั้งของร้าน)'],
    ['{ปิดรับเว็บ}', 'ปิดรับทางเว็บ', '21.30 น.', 'เวลาปิดรับรอบส่งวันถัดไปเมื่อสั่งทางเว็บ (ค่าตั้งของร้าน)'],
    ['{รายการราคา}', 'รายการราคา', 'ทดลองทาน 9 มื้อ 540 บาท\nแพ็ก 7 วัน 21 มื้อ 1,050 บาท (ตกมื้อละ 50 บาท)',
      'ราคาแพ็กจาก POS บรรทัดละแพ็ก: ถามราคาทั่วไป = ทดลอง + แพ็ก 7/15/30 วัน (พูดถึงเจ = แพ็กเจ) · ถามแพ็กเดียว = บรรทัดของแพ็กนั้น'],
    ['{แพ็กที่ลูกค้าถาม}', 'แพ็กที่ลูกค้าถาม', 'แพ็ก 7 วัน 21 มื้อ', 'ชื่อแพ็กที่ลูกค้าถาม (เฉพาะเมื่อถามแพ็กเดียว)'],
    ['{ราคารวมค่าส่ง}', 'ราคารวมค่าส่ง', '1,160 บาท',
      'ราคาแพ็กที่ลูกค้าถาม + ค่าส่ง (แพ็กเดียวที่ส่งรอบเดียวและรู้ค่าส่งแล้ว · ไม่มี = ตัดประโยคนั้นออก)', true],
    ['{วันเก็บรักษา}', 'เก็บได้กี่วัน', '120 วัน', 'เก็บในช่องฟรีซได้กี่วัน (ค่าตั้งของร้าน)'],
    ['{สถานะคำสั่งซื้อ}', 'สถานะคำสั่งซื้อ', 'คนขับรับอาหารแล้ว กำลังไปส่ง คาดว่าถึงประมาณ 12:40 น.',
      'สถานะจัดส่งล่าสุดจากระบบติดตาม Lalamove / DHL (ไม่มีคำสั่งซื้อที่กำลังส่ง = ไม่ใช้คำตอบนี้)'],
    ['{จำนวนเมนู}', 'จำนวนเมนู', '24 เมนู', 'จำนวนเมนูในเมนูรอบนี้ที่ยังเปิดขาย'],
    ['{แพ็กเดิม}', 'แพ็กเดิม', 'แพ็ก 7 วัน 21 มื้อ', 'แพ็กที่ลูกค้าซื้อครั้งล่าสุด (POS)'],
    ['{เมนูโปรด}', 'เมนูโปรด', 'กะเพราไก่สับ', 'เมนูที่ลูกค้าสั่งบ่อยสุดและยังเปิดขาย (POS)'],
    ['{เมนูใหม่}', 'เมนูใหม่', 'ไก่ย่างโคชูจัง', 'เมนูใหม่ในเมนูรอบนี้ หมวดเดียวกับเมนูโปรด'],
    ['{อาหารที่เลี่ยง}', 'อาหารที่เลี่ยง', 'กุ้ง', 'อาหารที่ลูกค้าแพ้/ไม่ทาน จากแชท แท็ก และโน้ตของทีม'],
    ['{วันส่งรอบก่อน}', 'วันส่งรอบก่อน', 'วันพฤหัสบดีที่ 15 ต.ค.', 'วันส่งของรอบที่ส่งช้าหรือส่งไม่สำเร็จ (ระบบติดตาม)'],
    ['{แพ็กแนะนำ}', 'แพ็กแนะนำ', 'แพ็ก 15 วัน 45 มื้อ', 'แพ็กที่ใหญ่กว่าที่ชวนเปลี่ยน'],
    ['{ราคาต่อมื้อแพ็กแนะนำ}', 'ราคาต่อมื้อแพ็กแนะนำ', '55.33 บาท', 'ราคาต่อมื้อของแพ็กที่ชวน (ราคาใน POS ÷ จำนวนมื้อ)'],
    ['{ถูกลงมื้อละ}', 'ถูกลงมื้อละ', '6.10 บาท', 'ถูกลงกี่บาทต่อมื้อเทียบแพ็กเดิม (ไม่ถูกลง = ตัดประโยคนั้นออก)', true],
    ['{มื้อที่เหลือ}', 'มื้อที่เหลือ', '4 มื้อ', 'มื้อที่ยังเหลือในแพ็กที่เปิดอยู่ (POS)'],
    ['{แพ็กที่ยังเหลือ}', 'แพ็กที่ยังเหลือ', 'แพ็ก 30 วัน 90 มื้อ', 'แพ็กที่ยังมีมื้อเหลือ (POS)'],
    ['{แพ็กทดลอง}', 'แพ็กทดลอง', 'ทดลองทาน 9 มื้อ 540 บาท',
      'แพ็กทดลองที่ถูกสุดที่ยังขายพร้อมราคา (POS · ไม่มีแพ็กทดลอง = ไม่ใช้คำตอบนี้)'],
    ['{แพ็กเล็กสุด}', 'แพ็กเล็กสุด', 'แพ็ก 7 วัน 21 มื้อ 1,050 บาท (ตกมื้อละ 50 บาท)',
      'แพ็กหลักที่เล็กสุดพร้อมราคาและราคาต่อมื้อ (POS · พูดถึงเจ = แพ็กเจที่เล็กสุด)'],
  ] as [string, string, string, string, boolean?][]).map(([key, label, sample, note, optional]): Placeholder => ({
    key, label, sample, note, scope: 'chat', optional: !!optional,
  })),
]

const CHAT_KEYS = new Set(MOCK_PLACEHOLDERS.filter((p) => p.scope === 'chat').map((p) => p.key))

/** Like api/app/ai/answers.py chat_only: a placeholder other than the name / the link (or an unknown one). */
export function chatOnly(body: string): boolean {
  return (body.match(/\{[^{}\s]{1,30}\}/g) ?? []).some((t) => CHAT_KEYS.has(t) || (t !== PH_NAME && t !== PH_LINK))
}

/** "{…}" that is not a placeholder (the API refuses to save it). */
export function unknownTokens(body: string): string[] {
  const known = new Set(MOCK_PLACEHOLDERS.map((p) => p.key))
  return [...new Set(body.match(/\{[^{}\s]{1,30}\}/g) ?? [])].filter((t) => !known.has(t))
}

/** api/app/ai/taxonomy.py SCRIPT_ANSWER_TYPES with their labels. */
export const MOCK_ANSWER_TYPES: Pick<AnswerTypeInfo, 'key' | 'label' | 'script'>[] = ([
  ['script_greeting', 'ทักทาย (ขึ้นต้นคำตอบ)', true], ['ship_fee', 'ค่าส่ง', false], ['ship_area', 'พื้นที่ส่ง', false],
  ['ship_date', 'วันได้ของ', false], ['price', 'ราคา', false], ['menu', 'เมนู', false], ['order_link', 'ลิงก์สั่ง', false],
  ['storage', 'เก็บรักษา', false], ['order_status', 'สถานะออเดอร์', false], ['payment', 'ชำระเงิน', false],
  ['change_order', 'แก้ไขออเดอร์', false], ['promo', 'โปรโมชั่น', false], ['health', 'สุขภาพ', false],
  ['allergy', 'แพ้อาหาร', false], ['complaint', 'ร้องเรียน', false], ['ordering', 'กำลังสั่ง', false],
  ['script_followup', 'ทักตาม · ลูกค้าเงียบ', true], ['script_followup_menu', 'ทักตาม · รอเลือกเมนู', true],
  ['script_followup_pay', 'ทักตาม · รอชำระ', true], ['script_opener', 'ทักลูกค้าเก่า', true],
  ['script_apology_late', 'ขอโทษ · รอบก่อนส่งช้า', true], ['script_apology_failed', 'ขอโทษ · รอบก่อนส่งไม่สำเร็จ', true],
  ['script_upsell', 'ชวนเปลี่ยนแพ็กใหญ่', true], ['script_open_pack', 'แพ็กเดิมยังเหลือ', true],
  ['script_area_closed', 'พื้นที่งดส่งชั่วคราว', true],
  // คู่มือปิดการขาย (api/app/ai/taxonomy.py PLAYBOOK_TYPES)
  ['pb_options', 'คู่มือ · ถามตัวเลือก', true], ['pb_price_objection', 'คู่มือ · ติดราคา', true],
  ['pb_hesitate', 'คู่มือ · ลังเล', true], ['pb_trial', 'คู่มือ · อยากลองก่อน', true],
  ['pb_quality', 'คู่มือ · ถามคุณภาพ', true], ['pb_agree', 'คู่มือ · ตกลง', true],
] as [string, string, boolean][]).map(([key, label, script]) => ({ key, label, script }))
